import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { CollapsibleCard } from './ui/CollapsibleCard';
import { 
  MessageCircle, 
  Send, 
  Copy, 
  Check, 
  Phone,
  CreditCard,
  CheckCircle2
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WhatsAppNotificationPanel = ({ operation, onRefresh }) => {
  const [whatsappStatus, setWhatsappStatus] = useState(null);
  const [waveLink, setWaveLink] = useState(operation?.wavePaymentLink || '');
  const [sendingPaymentLink, setSendingPaymentLink] = useState(false);
  const [sendingNotification, setSendingNotification] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    fetchWhatsAppStatus();
  }, []);

  useEffect(() => {
    setWaveLink(operation?.wavePaymentLink || '');
  }, [operation]);

  const fetchWhatsAppStatus = async () => {
    try {
      const token = 'cookie-auth' || localStorage.getItem('adminToken') || localStorage.getItem('token');
      const response = await axios.get(`${API}/notifications/whatsapp/status`, {});
      setWhatsappStatus(response.data);
    } catch (error) {
      console.error('Error fetching WhatsApp status:', error);
    }
  };

  const sendPaymentLink = async () => {
    if (!waveLink) {
      toast.error("Veuillez entrer un lien de paiement Wave");
      return;
    }

    setSendingPaymentLink(true);
    try {
      const token = 'cookie-auth' || localStorage.getItem('adminToken') || localStorage.getItem('token');
      const response = await axios.post(
        `${API}/notifications/whatsapp/send-payment-link`,
        {
          operation_id: operation.id,
          wave_link: waveLink
        },
        {}
      );

      if (response.data.success) {
        toast.success(`Lien envoyé au ${response.data.recipient}`);
        onRefresh?.();
      } else {
        throw new Error(response.data.error || "Échec de l'envoi");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || error.message);
    } finally {
      setSendingPaymentLink(false);
    }
  };

  const sendNotification = async (eventType) => {
    setSendingNotification(true);
    try {
      const token = 'cookie-auth' || localStorage.getItem('adminToken') || localStorage.getItem('token');
      const response = await axios.post(
        `${API}/notifications/whatsapp/send-notification`,
        {
          operation_id: operation.id,
          event_type: eventType
        },
        {}
      );

      if (response.data.success) {
        toast.success(`WhatsApp envoyé au ${response.data.recipient}`);
      } else {
        throw new Error(response.data.error || "Échec de l'envoi");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || error.message);
    } finally {
      setSendingNotification(false);
    }
  };

  const copyWaveLink = () => {
    if (waveLink) {
      navigator.clipboard.writeText(waveLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast.success("Lien Wave copié !");
    }
  };

  const getClientPhone = () => {
    return operation?.clientPhone || operation?.phone || operation?.userPhone || 'Non renseigné';
  };

  // Event types available for manual sending
  const notificationEvents = [
    { id: 'operation_created', label: 'Demande créée', icon: '📝' },
    { id: 'devis_validation_required', label: 'Devis à valider', icon: '📋' },
    { id: 'devis_accepted', label: 'Devis accepté', icon: '✅' },
    { id: 'ready_for_loading', label: 'Prêt chargement', icon: '🚚' },
    { id: 'chargement_started', label: 'Chargement', icon: '📦' },
    { id: 'en_route', label: 'En route', icon: '🛣️' },
    { id: 'delivered', label: 'Livré', icon: '🏁' },
    { id: 'payment_pending', label: 'Paiement attendu', icon: '💳' },
  ];

  if (!whatsappStatus?.enabled) {
    return (
      <Card className="border-dashed border-gray-300">
        <CardContent className="py-4 text-center text-gray-500">
          <MessageCircle className="h-8 w-8 mx-auto mb-2 opacity-50" />
          <p className="text-sm">WhatsApp non configuré</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <CollapsibleCard
      id="op-whatsapp"
      title="WhatsApp"
      icon={MessageCircle}
      defaultOpen={false}
      className="border-green-200 bg-green-50/30"
      headerClassName="text-green-700"
      headerBadge={<Badge variant="outline" className="bg-green-100 text-green-700 text-[10px] h-4 ml-1">Actif</Badge>}
      summaryText={getClientPhone() ? `Client : ${getClientPhone()}` : 'Notifications client'}
    >
      <div className="space-y-4">
        {/* Client info */}
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <Phone className="h-4 w-4" />
          <span>Client: {getClientPhone()}</span>
        </div>

        {/* Wave Payment Link Section */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
            <CreditCard className="h-4 w-4" />
            Lien de paiement Wave
          </label>
          <div className="flex gap-2">
            <Input
              value={waveLink}
              onChange={(e) => setWaveLink(e.target.value)}
              placeholder="https://pay.wave.com/m/..."
              className="flex-1 text-sm"
            />
            <Button
              variant="outline"
              size="icon"
              onClick={copyWaveLink}
              disabled={!waveLink}
              className="shrink-0"
            >
              {copied ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
            </Button>
          </div>
          <Button
            onClick={sendPaymentLink}
            disabled={!waveLink || sendingPaymentLink}
            className="w-full bg-green-600 hover:bg-green-700"
            size="sm"
          >
            {sendingPaymentLink ? (
              <>Envoi en cours...</>
            ) : (
              <>
                <Send className="h-4 w-4 mr-2" />
                Envoyer le lien par WhatsApp
              </>
            )}
          </Button>
        </div>

        {/* Quick Notifications */}
        <div className="border-t pt-3">
          <label className="text-sm font-medium text-gray-700 mb-2 block">
            Notifications rapides
          </label>
          <div className="grid grid-cols-2 gap-2">
            {notificationEvents.map((event) => (
              <Button
                key={event.id}
                variant="outline"
                size="sm"
                onClick={() => sendNotification(event.id)}
                disabled={sendingNotification}
                className="text-xs justify-start h-8 px-2"
              >
                <span className="mr-1">{event.icon}</span>
                <span className="truncate">{event.label}</span>
              </Button>
            ))}
          </div>
        </div>

        {/* Wave Link saved indicator */}
        {operation?.wavePaymentLink && (
          <div className="flex items-center gap-2 text-xs text-green-600 bg-green-100 rounded px-2 py-1">
            <CheckCircle2 className="h-3 w-3" />
            Lien Wave enregistré
          </div>
        )}
      </div>
    </CollapsibleCard>
  );
};

export default WhatsAppNotificationPanel;
